// The armoury: every gun and grenade from the Godot build (data/weapons, data/throwables),
// ported to the shared sim and retuned for the browser game. Pure data + small helpers; the
// per-tick firing rules live in player.ts (predicted) and arsenal.ts (server resolution).
//
// Slots (GAMEPLAY_LOOP §1.8 / §2.5): PRIMARY and SECONDARY are picked in the saloon; SPECIAL
// weapons are never loadout picks, they're gifts from the run (ordnance crates), and they
// recharge one shot per `recharge` points of damage your shield soaks (§1.9).

import { secs } from './core';
import { AP, ClassId } from './defs';

export const enum WSlot { Primary = 0, Secondary = 1, Special = 2 }
export const enum Fire { Semi = 0, Auto = 1, Burst = 2, Charge = 3 }
/** How a shot leaves the gun. Hitscan resolves instantly; the rest are flown projectiles. */
export const enum Proj { Hitscan = 0, Rocket = 1, Harpoon = 2, Firework = 3, Beacon = 4, Turret = 5, Seeker = 6 }
/** Elemental proc carried by ordnance. Matches the renderer's El ids where one exists. */
export const enum Elem { None = -1, Fire = 0, Acid = 1, Shock = 2, Ice = 3, Magnetic = 8, Rainbow = 9 }
/** What a firework shell rains at apex (FIREWORKS_DESIGN.md). */
export const enum Payload { None = 0, Seekers = 1, Canopy = 2, Finale = 3 }
/** Canopy flavours: a 15 s zone. Elemental ones hurt robots, the rest help the posse. */
export const enum Canopy { Fire = 0, Ice = 1, Shock = 2, Acid = 3, Magnetic = 4, Aegis = 5, Coolant = 6, Mending = 7 }

export interface WeaponDef {
  id: number;
  name: string;
  /** subheading in the loadout ("Double Barrel Shotgun" under "The Gentlemen") */
  sub: string;
  /** ≤ 14 chars for the HUD plate */
  short: string;
  slot: WSlot;
  role: 'ANTI-HORDE' | 'ANTI-ARMOR' | 'ALL-ROUND' | 'SUPPORT';
  blurb: string;
  damage: number;
  pellets: number;
  fire: Fire;
  interval: number; // ticks between shots (between bursts for Burst)
  burst: number;
  burstGap: number; // ticks between rounds inside a burst
  charge: number; // ticks to full charge (Charge)
  mag: number;
  reloadTicks: number; // whole mag, or per round when `rounds`
  reloadHeat: number; // whole mag, or per round when `rounds`
  rounds: boolean;
  /** the gun has no magazine: it never spends a round and never reloads (the Duel Blaster runs on heat) */
  noAmmo?: boolean;
  /** heat the pack takes per shot, scaled by the charge (the Duel Blaster's limiter) */
  shotHeat?: number;
  spreadDeg: number; // hip
  adsSpreadDeg: number; // aiming down sights
  ap: AP;
  range: number;
  /** damage falls off linearly from falloffStart to falloffEnd down to falloffMin × */
  falloffStart: number; falloffEnd: number; falloffMin: number;
  headMul: number;
  /** × damage against shields (plasma: Duel Blaster) */
  shieldMul: number;
  /** muzzle speed m/s for bullet drop (BULLET_GRAVITY); 0 = no drop (plasma, tracked rounds, hitscan lasers) */
  muzzleV: number;
  /** knockback per hit (m/s on a weight-1 robot) */
  push: number;
  /** seconds to swap to it */
  draw: number;
  akimbo: boolean;
  pistol: boolean;
  /** first round into an empty mag throws a 360° shove (Bolt-Action Sniper) */
  emptyShove: number;
  // ---- ordnance
  proj: Proj;
  elem: Elem;
  payload: Payload;
  canopy: Canopy;
  blast: number; // blast radius for ordnance
  projSpeed: number;
  /** special only: one shot back per this much damage your shield soaks */
  recharge: number;
  // ---- feel (client): camera kick and pad rumble per shot
  kick: number;
  rumble: number;
  // ---- identity (GUN_IDENTITIES.md): what makes this gun play differently
  perk: Perk;
  /** tracking rounds: the cone (degrees off the crosshair) a round will bend to find a robot.
   *  Tracked rounds hit the body only (no headshots) and carry the gun's lower damage. */
  track: number;
}

/** Each gun's signature mechanic. */
export const enum Perk {
  None = 0,
  /** Pulse Rifle: land all three rounds of a burst on robots and a fourth overload pulse follows */
  Overload,
  /** Auto MG: fire rate spins up from SPIN_MIN_RATE to SPIN_MAX_RATE while held; bloom grows with it */
  SpinUp,
  /** Bolt-Action Sniper: holding aim builds a dead-eye bonus, spent on the next shot */
  Steady,
  /** Big Iron: hip taps fan the hammer at double rate but bloom; aimed shots are slow and true */
  Fan,
  /** The Gentlemen: most of a shell into one robot at arm's length staggers it */
  Stagger,
  /** The Scoundrel: aimed, both barrels go at once as a tight, staggering cluster */
  DoubleBarrel,
  /** Duel Blaster: release early for a straight bolt; a full charge fires a tracking plasma */
  PlasmaLock,
}

// ---- perk tuning (balanced in /mnt/project-files/weapons/GUN_IDENTITIES.md)
export const SPIN_TICKS = secs(1.2);
/** Auto MG ticks between rounds at zero spin and at full spin (5/s → 15/s) */
export const SPIN_SLOW = 12, SPIN_FAST = 4;
export const STEADY_TICKS = secs(1.2), STEADY_BONUS = 0.6;
/** Big Iron hip cadence (fanning), bloom added per hip shot and bled per second, spread per bloom */
export const FAN_INTERVAL = secs(0.21), FAN_BLOOM = 0.6, FAN_BLEED = 1.6, FAN_SPREAD = 1.5;
/** Pulse Rifle overload pulse: damage and stagger */
export const OVERLOAD_DMG = 22, OVERLOAD_STUN = secs(0.5);
/** point-blank stagger (Gentlemen, Scoundrel): pellets in one robot inside STAGGER_RANGE */
export const STAGGER_RANGE = 5, STAGGER_STUN = secs(0.6);
/** Duel Blaster: shortest charge that fires, partial-charge damage range, full-charge tracked bonus */
export const PLASMA_MIN = secs(0.15), PLASMA_PARTIAL_MIN = 0.4, PLASMA_FULL_MUL = 1.3;
/** what a shot did beyond the gun's base (StepOut.mod) */
export const enum ShotMod { Normal = 0, Partial = 1, Locked = 2, Double = 3 }
/** pellets that must land in one robot to stagger it */
export function staggerPellets(w: WeaponDef, mod: number) { return Math.ceil(w.pellets * (mod === ShotMod.Double ? 2 : 1) * 0.55); }

const D = (w: Partial<WeaponDef> & Pick<WeaponDef, 'id' | 'name' | 'slot' | 'damage'>): WeaponDef => ({
  sub: '', short: w.name.toUpperCase().slice(0, 14), role: 'ALL-ROUND', blurb: '',
  pellets: 1, fire: Fire.Semi, interval: secs(0.2), burst: 1, burstGap: 0, charge: 0,
  mag: 12, reloadTicks: secs(1.4), reloadHeat: 20, rounds: false,
  spreadDeg: 2, adsSpreadDeg: 0.8, ap: AP.Medium, range: 160,
  falloffStart: 999, falloffEnd: 1000, falloffMin: 1, headMul: 2, shieldMul: 1, push: 0.6, muzzleV: 110,
  draw: 0.35, akimbo: false, pistol: false, emptyShove: 0,
  proj: Proj.Hitscan, elem: Elem.None, payload: Payload.None, canopy: Canopy.Fire, blast: 0, projSpeed: 0, recharge: 0,
  kick: 0.04, rumble: 0, perk: Perk.None, track: 0,
  ...w,
});

export const enum W {
  Starter = 0, PulseRifle, AutoMG, BoltSniper, Gentlemen,
  BigIron, DuelBlaster, Akimbo, Scoundrel,
  Rocket, RocketFire, RocketIce, RocketShock, RocketAcid, RocketMag,
  Harpoon, BigOne, BadIdea, Turret,
  LightSeeker, EmberBloom, FrostBloom, StormBloom, CausticBloom, MagnetBloom, Rainbow,
  EmberCanopy, FrostCanopy, StormCanopy, CausticCanopy, MagnetCanopy, AegisCanopy, CoolantRain, MendingRain,
}
export const NO_WEAPON = 255;

const rocket = (id: number, name: string, short: string, elem: Elem, sub: string) => D({
  id, name, short, sub, slot: WSlot.Special, role: 'ANTI-ARMOR', damage: 120, ap: AP.Ordnance, mag: 1, interval: secs(0.6),
  proj: Proj.Rocket, elem, blast: 5, projSpeed: 42, recharge: 100, kick: 0.3, rumble: 0.8, draw: 0.5,
  blurb: 'One heavy-ordnance rocket. Cracks anything, Metal Brutes included.',
});
const bloom = (id: number, name: string, elem: Elem, sub: string) => D({
  id, name, sub, slot: WSlot.Special, role: 'ANTI-HORDE', damage: 50, ap: AP.Light, mag: 1, interval: secs(0.8),
  proj: Proj.Firework, payload: Payload.Seekers, elem, blast: 20, projSpeed: 18, recharge: 100, kick: 0.08,
  blurb: 'Lob it, it launches, it bursts into homing seekers that pin robots for a second.',
});
const canopy = (id: number, name: string, c: Canopy, sub: string, role: WeaponDef['role'] = 'SUPPORT') => D({
  id, name, sub, slot: WSlot.Special, role, damage: 0, ap: AP.Light, mag: 1, interval: secs(0.8),
  proj: Proj.Firework, payload: Payload.Canopy, canopy: c, blast: 7, projSpeed: 18, recharge: 100, kick: 0.08,
  blurb: 'Lob it, it launches, it bursts into a 15 second canopy over the spot.',
});

export const WEAPONS: WeaponDef[] = [
  // ---------------------------------------------------------------- primaries
  D({ id: W.Starter, name: 'The Starter', sub: 'Standard-Issue Pistol', slot: WSlot.Primary, pistol: true,
    damage: 12, interval: secs(0.16), mag: 12, reloadTicks: secs(1.2), reloadHeat: 18, spreadDeg: 2.5, adsSpreadDeg: 1.0, ap: AP.Light,
    range: 70, falloffStart: 20, falloffEnd: 55, falloffMin: 0.4, draw: 0.25, kick: 0.04, rumble: 0.15, track: 14,
    blurb: 'Tracking rounds: anything near the crosshair gets hit, body only. Easy, steady, never the hardest hitter.' }),
  D({ id: W.PulseRifle, name: 'Pulse Rifle', slot: WSlot.Primary, role: 'ALL-ROUND',
    damage: 13, fire: Fire.Burst, burst: 3, burstGap: secs(0.065), interval: secs(0.3), mag: 36, reloadTicks: secs(1.6), reloadHeat: 26,
    spreadDeg: 1.8, adsSpreadDeg: 0.6, ap: AP.Medium, muzzleV: 120, range: 160, falloffStart: 25, falloffEnd: 65, falloffMin: 0.4, kick: 0.035, rumble: 0.12,
    perk: Perk.Overload,
    blurb: 'Three-round bursts. Land all three on a robot and a free overload pulse follows that shocks it stiff.' }),
  D({ id: W.AutoMG, name: 'Auto MG', slot: WSlot.Primary, role: 'ANTI-HORDE',
    damage: 10, fire: Fire.Auto, interval: SPIN_FAST, mag: 75, reloadTicks: secs(2.6), reloadHeat: 40, perk: Perk.SpinUp,
    spreadDeg: 3.0, adsSpreadDeg: 1.4, ap: AP.Medium, muzzleV: 130, range: 120, falloffStart: 15, falloffEnd: 55, falloffMin: 0.3, draw: 0.5, push: 0.4,
    kick: 0.03, rumble: 0.18,
    blurb: 'Spins up from 5 to 15 rounds a second while you hold it, and blooms as it goes. Commit, then hose.' }),
  D({ id: W.BoltSniper, name: 'Bolt-Action Sniper', short: 'BOLT-ACTION', slot: WSlot.Primary, role: 'ANTI-ARMOR',
    damage: 90, interval: secs(1.0), mag: 5, reloadTicks: secs(0.7), reloadHeat: 5.6, rounds: true,
    spreadDeg: 2.5, adsSpreadDeg: 0, ap: AP.Heavy, muzzleV: 260, range: 240, falloffStart: 60, falloffEnd: 180, falloffMin: 0.75, headMul: 2.5, push: 3,
    draw: 0.6, emptyShove: 13, kick: 0.2, rumble: 0.6, perk: Perk.Steady,
    blurb: 'Aim it (L2) or miss. Hold aim to steady the shot: up to +60% when the chevrons close. Racking into an empty gun shoves everything away.' }),
  D({ id: W.Gentlemen, name: 'The Gentlemen', sub: 'Double Barrel Shotgun', short: 'GENTLEMEN', slot: WSlot.Primary, role: 'ANTI-HORDE',
    damage: 14, pellets: 12, interval: secs(0.45), mag: 5, reloadTicks: secs(0.75), reloadHeat: 6.4, rounds: true,
    spreadDeg: 5.5, adsSpreadDeg: 3.0, ap: AP.Light, range: 40, falloffStart: 8, falloffEnd: 22, falloffMin: 0.2, push: 2.2, draw: 0.55,
    kick: 0.16, rumble: 0.55, perk: Perk.Stagger,
    blurb: 'The ring is the spread; aim (L2) to tighten it. Most of a shell into a robot at arm’s length staggers it.' }),
  // ---------------------------------------------------------------- secondaries
  D({ id: W.BigIron, name: 'Big Iron', slot: WSlot.Secondary, role: 'ANTI-ARMOR', pistol: true,
    damage: 55, interval: secs(0.42), mag: 6, reloadTicks: secs(0.45), reloadHeat: 5.2, rounds: true, perk: Perk.Fan,
    spreadDeg: 0.9, adsSpreadDeg: 0.3, ap: AP.AntiTank, muzzleV: 150, range: 160, falloffStart: 30, falloffEnd: 80, falloffMin: 0.55, headMul: 2.2, push: 2,
    kick: 0.12, rumble: 0.45,
    blurb: 'AP4 heavy revolver. Hip taps fan the hammer twice as fast but scatter; aimed, it is slow and dead true.' }),
  D({ id: W.DuelBlaster, name: 'Duel Blaster', short: 'DUEL BLASTER', slot: WSlot.Secondary, role: 'ALL-ROUND', pistol: true,
    damage: 34, fire: Fire.Charge, charge: secs(0.7), interval: secs(0.1), mag: 6, reloadTicks: secs(1.2), reloadHeat: 0, perk: Perk.PlasmaLock, track: 16, noAmmo: true, shotHeat: 9,
    spreadDeg: 0.4, adsSpreadDeg: 0, ap: AP.Medium, muzzleV: 0, range: 90, falloffStart: 35, falloffEnd: 60, falloffMin: 0.8, shieldMul: 2, push: 1,
    kick: 0.07, rumble: 0.3,
    blurb: 'Twin plasma bolts that lock on and hunt their robot. No magazine: every shot heats the pack. Charge it for full damage. Double damage to shields.' }),
  D({ id: W.Akimbo, name: 'Akimbo Pistols', short: 'AKIMBO', slot: WSlot.Secondary, role: 'ANTI-HORDE', pistol: true, akimbo: true,
    damage: 8, fire: Fire.Auto, interval: secs(0.11), mag: 28, reloadTicks: secs(1.6), reloadHeat: 22, track: 18,
    spreadDeg: 3.2, adsSpreadDeg: 2.0, ap: AP.Light, range: 70, falloffStart: 18, falloffEnd: 42, falloffMin: 0.3, draw: 0.3,
    kick: 0.03, rumble: 0.12,
    blurb: 'Two pistols of tracking rounds. Hold the trigger near anything and it gets hit. Low damage, zero effort.' }),
  D({ id: W.Scoundrel, name: 'The Scoundrel', sub: 'Sawed-Off Shotgun', short: 'SCOUNDREL', slot: WSlot.Secondary, role: 'ANTI-HORDE', pistol: true,
    damage: 13, pellets: 8, interval: secs(0.2), mag: 2, reloadTicks: secs(0.6), reloadHeat: 10, rounds: true,
    spreadDeg: 8.5, adsSpreadDeg: 3.2, ap: AP.Light, range: 30, falloffStart: 5, falloffEnd: 16, falloffMin: 0.15, push: 3.5, draw: 0.15,
    kick: 0.2, rumble: 0.7, perk: Perk.DoubleBarrel,
    blurb: 'Hip fires one barrel wide. Aim (L2) and both barrels go at once, tight, with a stagger. Fastest draw in the saloon.' }),
  // ---------------------------------------------------------------- specials (ordnance crates)
  rocket(W.Rocket, 'Rocket Launcher', 'ROCKET LNCHR', Elem.None, 'Heavy Ordnance'),
  rocket(W.RocketFire, 'Fire Rocket Launcher', 'FIRE ROCKET', Elem.Fire, 'Every robot caught catches fire'),
  rocket(W.RocketIce, 'Ice Rocket Launcher', 'ICE ROCKET', Elem.Ice, 'Every robot caught is frozen'),
  rocket(W.RocketShock, 'Shock Rocket Launcher', 'SHOCK ROCKET', Elem.Shock, 'Every robot caught is shocked'),
  rocket(W.RocketAcid, 'Acid Rocket Launcher', 'ACID ROCKET', Elem.Acid, 'Every robot caught has its armor eaten'),
  rocket(W.RocketMag, 'Magnetic Rocket Launcher', 'MAG ROCKET', Elem.Magnetic, 'Every robot caught loses its shield'),
  D({ id: W.Harpoon, name: 'Harpoon Gun', short: 'HARPOON', slot: WSlot.Special, role: 'ANTI-ARMOR', damage: 45, ap: AP.AntiTank, // Godot harpoon.tres AP3
    mag: 3, interval: secs(1.2), proj: Proj.Harpoon, blast: 4, projSpeed: 60, recharge: 40, push: 8, kick: 0.15, rumble: 0.5,
    blurb: 'Impales a robot, pins it for a beat, and vents a gas cloud around it. Three bolts.' }),
  D({ id: W.BigOne, name: 'The Big One', sub: 'Finale Shell', short: 'THE BIG ONE', slot: WSlot.Special, role: 'ANTI-ARMOR', damage: 500,
    ap: AP.Ordnance, mag: 1, interval: secs(1), proj: Proj.Beacon, payload: Payload.Finale, blast: 12, projSpeed: 18, recharge: 180,
    kick: 0.05, rumble: 0.2,
    blurb: 'Lob the beacon. A red laser marks the spot for three seconds. Then the sky falls on it.' }),
  D({ id: W.BadIdea, name: 'Bad Idea', sub: 'Heat-Seeking Siege Launcher', short: 'BAD IDEA', slot: WSlot.Special, role: 'ANTI-ARMOR',
    damage: 400, ap: AP.Ordnance, mag: 1, interval: secs(1), proj: Proj.Rocket, elem: Elem.Rainbow, blast: 16, projSpeed: 30, recharge: 250,
    range: 600, kick: 0.6, rumble: 1,
    blurb: 'Seeks the biggest robot in sight. The blast is enormous and it does not care who fired it.' }),
  D({ id: W.Turret, name: 'Turret Deployer', short: 'TURRET', sub: 'Single-use auto-turret', slot: WSlot.Special, role: 'SUPPORT', damage: 10,
    ap: AP.Medium, mag: 1, interval: secs(1), proj: Proj.Turret, range: 32, recharge: 120, kick: 0.02,
    blurb: 'Plants a G.U.L.P. sentry two metres ahead. It shoots whatever is closest for 20 seconds.' }),
  bloom(W.LightSeeker, 'Light Seeker', Elem.None, 'Seeker Shell'),
  bloom(W.EmberBloom, 'Ember Bloom', Elem.Fire, 'Seeker Shell: Fire'),
  bloom(W.FrostBloom, 'Frost Bloom', Elem.Ice, 'Seeker Shell: Ice'),
  bloom(W.StormBloom, 'Storm Bloom', Elem.Shock, 'Seeker Shell: Shock'),
  bloom(W.CausticBloom, 'Caustic Bloom', Elem.Acid, 'Seeker Shell: Acid'),
  bloom(W.MagnetBloom, 'Magnet Bloom', Elem.Magnetic, 'Seeker Shell: Magnetic'),
  { ...bloom(W.Rainbow, 'Rainbow Cascade', Elem.Rainbow, 'Seeker Shell: all five elements'), short: 'CASCADE' },
  canopy(W.EmberCanopy, 'Ember Canopy', Canopy.Fire, 'Canopy Shell: burning rain', 'ANTI-HORDE'),
  canopy(W.FrostCanopy, 'Frost Canopy', Canopy.Ice, 'Canopy Shell: freezing rain', 'ANTI-HORDE'),
  canopy(W.StormCanopy, 'Storm Canopy', Canopy.Shock, 'Canopy Shell: lightning rain', 'ANTI-HORDE'),
  canopy(W.CausticCanopy, 'Caustic Canopy', Canopy.Acid, 'Canopy Shell: acid rain', 'ANTI-ARMOR'),
  canopy(W.MagnetCanopy, 'Magnet Canopy', Canopy.Magnetic, 'Canopy Shell: shield-killing rain', 'ANTI-ARMOR'),
  canopy(W.AegisCanopy, 'Aegis Canopy', Canopy.Aegis, 'Canopy Shell: dome blocks incoming fire'),
  canopy(W.CoolantRain, 'Coolant Rain', Canopy.Coolant, 'Canopy Shell: bleeds heat off the posse'),
  canopy(W.MendingRain, 'Mending Rain', Canopy.Mending, 'Canopy Shell: heals the posse inside'),
];

export const PRIMARIES = WEAPONS.filter((w) => w.slot === WSlot.Primary).map((w) => w.id);
export const SECONDARIES = WEAPONS.filter((w) => w.slot === WSlot.Secondary).map((w) => w.id);
export const SPECIALS = WEAPONS.filter((w) => w.slot === WSlot.Special).map((w) => w.id);

// ------------------------------------------------------------------ grenades
export const enum G { Impact = 0, Fire, Ice, Shock, Acid, Magnetic, Shrapnel, Stun, Gas, Time }
export interface GrenadeDef {
  id: number;
  name: string;
  blurb: string;
  damage: number;
  ap: AP;
  radius: number;
  elem: Elem;
  /** seconds of hard stun on everything caught (heavies get a third) */
  stun: number;
  /** seconds of 50 % slow */
  slow: number;
  /** gas cloud: seconds, dps (bypasses shields and armor) */
  gasSecs: number; gasDps: number;
  /** seconds from pulling the pin; 0 = goes off on impact. Hold R1 to cook it, hold too long and
   *  it goes off in your hand. */
  fuse: number;
  /** sticks to the first robot it touches and rides it until the fuse runs out */
  sticky: boolean;
}
const N = (g: Partial<GrenadeDef> & Pick<GrenadeDef, 'id' | 'name' | 'blurb'>): GrenadeDef => ({
  damage: 0, ap: AP.Ordnance, radius: 5, elem: Elem.None, stun: 0, slow: 0, gasSecs: 0, gasDps: 0, fuse: 0, sticky: false, ...g,
});
export const GRENADES: GrenadeDef[] = [
  N({ id: G.Impact, name: 'Impact Grenade', blurb: 'Goes off the moment it hits anything. Raw blast that cracks even a Metal Brute.', damage: 85, radius: 5 }),
  N({ id: G.Fire, name: 'Fire Grenade', blurb: 'Blast that sets everything caught on fire. 2.5 s fuse: cook it.', damage: 60, radius: 5, elem: Elem.Fire, fuse: 2.5 }),
  N({ id: G.Ice, name: 'Ice Grenade', blurb: 'Blast that freezes small robots solid and slows the big ones. 2.5 s fuse.', damage: 50, radius: 5, elem: Elem.Ice, fuse: 2.5 }),
  N({ id: G.Shock, name: 'Shock Grenade', blurb: 'Blast that stuns, then arcs lightning to robots nearby. Short 1.5 s fuse.', damage: 50, radius: 5, elem: Elem.Shock, fuse: 1.5 }),
  N({ id: G.Acid, name: 'Acid Grenade', blurb: 'Blast that eats a tier of armor and softens everything caught. 2.5 s fuse.', damage: 50, radius: 5, elem: Elem.Acid, fuse: 2.5 }),
  N({ id: G.Magnetic, name: 'Magnetic Grenade', blurb: 'Sticks to the first robot it touches, then wipes shields and locks them off. 2 s fuse.', damage: 50, radius: 5, elem: Elem.Magnetic, fuse: 2, sticky: true }),
  N({ id: G.Shrapnel, name: 'Shrapnel Grenade', blurb: 'The classic frag: twice the reach, weak against armor, long 3.5 s fuse to cook.', damage: 55, radius: 9, ap: AP.Medium, fuse: 3.5 }),
  N({ id: G.Stun, name: 'Stun Grenade', blurb: 'Goes off on impact. No damage: everything caught stands there for four seconds.', radius: 6, stun: 4, ap: AP.Light }),
  N({ id: G.Gas, name: 'Gas Grenade', blurb: 'Pops a cloud that slows and eats through shields and armor for seven seconds. 2 s fuse.', radius: 4.8, gasSecs: 7, gasDps: 14, ap: AP.Light, fuse: 2 }),
  N({ id: G.Time, name: 'Time Grenade', blurb: 'Goes off on impact: drags everything caught to half speed for ten seconds, posse included.', radius: 5.5, slow: 10, ap: AP.Light }), // Nick 2026-10-22: on impact (the 3 s fuse let everything walk out of it)
];
/** a Time grenade drags whatever it catches, robots and cowboys alike, to this speed */
export const TIME_SLOW_MUL = 0.33; // playtest 2 (Nick): a third, not half
export const GRENADE_MAX = 3;
/** the Duel Blaster's bolts (Godot blaster_pistol.tres homing_turn_rate_deg 206): slow plasma that curves onto its mark */
export const DUEL_BOLT_SPEED = 23.4; // playtest 3 (Nick): a tenth slower, so the flight reads
export const DUEL_BOLT_TURN = 3.96; // rad/s, a tenth more tracking
/** bullet drop: every ballistic round falls under this (Godot bolts sag; FLIGHT_DESIGN keeps the sniper near flat) */
export const BULLET_GRAVITY = 9.8;
export const DROP_SEG = 12; // metres per straight segment of the arc
/** rounds fly flat to here (Godot solved its bolt gravity so the shot lands on the aim point at the aim range), then sag */
export const DROP_FREE = 35;
/** shield damage soaked per grenade earned back (GAMEPLAY_LOOP §1.2, GRENADE_RECHARGE_SHIELD_DAMAGE) */
export const GRENADE_BANK = 50;
export const THROW_SPEED = 19;
export const THROW_CD = secs(0.6);
export const GRENADE_GRAVITY = 22;

// ------------------------------------------------------------------ loadout
export interface Loadout { primary: number; secondary: number; grenade: number }

export const CLASS_LOADOUT: Record<ClassId, Loadout> = {
  [ClassId.Gunslinger]: { primary: W.BoltSniper, secondary: W.BigIron, grenade: G.Impact },
  [ClassId.Valkyrie]: { primary: W.PulseRifle, secondary: W.DuelBlaster, grenade: G.Fire },
  [ClassId.Phantom]: { primary: W.Gentlemen, secondary: W.Akimbo, grenade: G.Stun },
  [ClassId.Juggernaut]: { primary: W.AutoMG, secondary: W.Scoundrel, grenade: G.Shrapnel },
};

export function validLoadout(l: Partial<Loadout> | null | undefined, cls: ClassId): Loadout {
  const d = CLASS_LOADOUT[cls] ?? CLASS_LOADOUT[ClassId.Gunslinger];
  const pri = l && WEAPONS[l.primary!]?.slot === WSlot.Primary ? l.primary! : d.primary;
  const sec = l && WEAPONS[l.secondary!]?.slot === WSlot.Secondary ? l.secondary! : d.secondary;
  const nade = l && l.grenade! >= 0 && l.grenade! < GRENADES.length ? l.grenade! : d.grenade;
  return { primary: pri, secondary: sec, grenade: nade };
}

export function weaponDef(id: number): WeaponDef {
  return WEAPONS[id] ?? WEAPONS[W.Starter];
}

/** Ordnance-crate pool, weighted: rockets and seekers common, the Big One rare, Bad Idea a legend. */
export const CRATE_POOL: [number, number][] = [
  [W.Rocket, 6], [W.RocketFire, 2], [W.RocketIce, 2], [W.RocketShock, 2], [W.RocketAcid, 2], [W.RocketMag, 2],
  [W.Harpoon, 4], [W.Turret, 4], [W.BigOne, 1.5], [W.BadIdea, 0.4],
  [W.LightSeeker, 3], [W.EmberBloom, 1.5], [W.FrostBloom, 1.5], [W.StormBloom, 1.5], [W.CausticBloom, 1.5], [W.MagnetBloom, 1.5], [W.Rainbow, 0.8],
  [W.EmberCanopy, 1.5], [W.FrostCanopy, 1.5], [W.StormCanopy, 1.5], [W.CausticCanopy, 1.2], [W.MagnetCanopy, 1.2],
  [W.AegisCanopy, 1.5], [W.CoolantRain, 1.5], [W.MendingRain, 2],
];
export function rollCrate(r: number): number {
  let total = 0;
  for (const [, w] of CRATE_POOL) total += w;
  let x = r * total;
  for (const [id, w] of CRATE_POOL) { if ((x -= w) <= 0) return id; }
  return W.Rocket;
}
